<script setup lang="ts">
import { computed } from 'vue'
import { useConfigStore } from '@/stores/configStore'
import { useMaterialStore } from '@/stores/materialStore'
import { dataService } from '@/services/DataService'
import { PREORDER_URL } from '@/env'
import PriceDisplay from './PriceDisplay.vue'

defineOptions({
  name: 'SummaryContent'
})

// Stores
const configStore = useConfigStore()
const materialStore = useMaterialStore()

// Computed
const selectedGrade = computed(() => configStore.currentGrade)

const selectedExteriorColor = computed(() => {
  if (!materialStore.selectedExteriorColor) return null
  return materialStore.exteriorColors.find(c => c.material === materialStore.selectedExteriorColor) || null
})

/**
  * Construit l'URL de précommande avec les paramètres dynamiques
  * Format: https://[env].suzuki.fr/vehicule/evitara/precommande/?version={{ grade }}&teinte={{ color }}&model={{ motorization - battery }}
  */
const preorderUrl = computed(() => {
  const baseUrl = `${PREORDER_URL}/vehicule/evitara/precommande/?`

  // Récupérer les valeurs
  const gradeName = configStore.currentGrade?.fullName || ''
  const colorLabel = getExteriorColorLabel()
  // const priceValue = configStore.formattedTotalPrice || ''
  const motorization = configStore.currentGrade?.motorization || ''
  const battery = configStore.currentGrade?.battery || ''

  // Construire le modèle (motorisation + batterie)
  const model = [motorization, battery].filter(Boolean).join(' - ')

  // Nettoyer le prix pour l'URL (enlever le symbole €)
  // const cleanPrice = priceValue.replace(/[€\s]/g, '').trim()

  // Encoder les paramètres URL
  const versionParam = `version=${encodeURIComponent(gradeName)}`
  const teinteParam = `teinte=${encodeURIComponent(colorLabel)}`
  // const prixParam = `prix=${encodeURIComponent(cleanPrice)}`
  const modelParam = `model=${encodeURIComponent(model)}`

  return `${baseUrl}${versionParam}&${modelParam}&${teinteParam}`
})

/**
  * Réinitialise la configuration et renvoie à l'étape de sélection de la version
  */
const emit = defineEmits<{
  reset: []
}>()

const handleReset = () => {
  configStore.resetConfig()
  materialStore.clearMaterials()
  emit('reset')
}

/**
 * Retourne le label de la couleur extérieure sélectionnée
 * Ex: "Bluish Black Pearl"
 * Récupère le label depuis la configuration des items
 */
const getExteriorColorLabel = (): string => {
  if (!materialStore.selectedExteriorColor) return ''
  
  // Extraire le code couleur du path (ex: BC_WB3 de EXT-GRD_P22GLX-BG_A-BC_WB3)
  const colorCodeMatch = materialStore.selectedExteriorColor.match(/BC_[A-Z0-9_]+/)
  const colorCode = colorCodeMatch ? colorCodeMatch[0] : null
  
  if (!colorCode) {
    return materialStore.selectedExteriorColor
  }
  
  // Récupérer les items pour trouver le label
  try {
    const itemsConfig = dataService.getItems()
    
    // Chercher l'item correspondant dans la catégorie BODYCOLOR
    for (const category of itemsConfig.ITEMS) {
      if (category.category_id === 'BODYCOLOR') {
        for (const group of category.groups) {
          const item = group.items.find(i => i.item_id === colorCode)
          if (item && item.label) {
            return item.label
          }
        }
      }
    }
  } catch (error) {
    console.warn('Erreur lors de la récupération du label de la couleur:', error)
  }
  
  // Fallback: retourner le code couleur
  return colorCode
}
</script>

<template>
  <div class="summary-content">
    <header class="summary-header">
      <h3>Votre configuration :</h3>
    </header>

    <div class="summary-body">
      <!-- Grade sélectionné -->
      <section class="summary-section">
        <!-- <h4>Version</h4> -->
        <div v-if="selectedGrade" class="grade-info">
          <h4 class="grade-name">{{ selectedGrade.fullName }}</h4>
          <p v-if="selectedGrade.motorization" class="grade-motorization">{{ selectedGrade.motorization }}</p>
          <p v-if="selectedGrade.battery" class="grade-battery">{{ selectedGrade.battery }}</p>
          <h5 class="title-color">Couleur</h5>
          <p v-if="selectedExteriorColor" class="grade-color">{{ getExteriorColorLabel() }}</p>
        <PriceDisplay />
        </div>
        <p v-else class="empty">Aucune version sélectionnée</p>
      </section>
    </div>

    <!-- Bouton Reset -->
    <div class="preorder-section">
      <button class="reset-btn" @click="handleReset">
        Modifier
      </button>

    <!-- Bouton Précommander -->
      <a
        :href="preorderUrl"
        class="precommand-link"
      >
        Précommander
      </a>
    </div>
  </div>
</template>

<style scoped>

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 0 20px;
}

.edit-btn {
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: white;
  color: #6c757d;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.edit-btn:hover {
  border-color: #007bff;
  color: #007bff;
  background: #e7f1ff;
}

.preorder-section {
  padding: 1rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
}

.reset-btn {
  margin: 0;
  padding: 0.5em 3em;
  background-color: transparent;
  color: var(--color-text-primary);
  border: solid 1px var(--color-text-primary);
  border-radius: 0.25rem;
  font-size: 1rem;
  line-height: var(--line-height-relaxed);
  font-weight: 300;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
  letter-spacing: 0.5px;
  text-decoration: none;
}

.reset-btn:hover:not(:disabled) {
  background-color: var(--color-bg-panel);
  /* color: var(--color-primary); */
}

.reset-btn:active {
  color: var(--color-primary);
}

.reset-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.precommand-link {
  padding: 0.5em 3em;
  background-color: var(--color-text-primary);
  color: var(--color-primary);
  border: solid 1px var(--color-text-primary);
  border: none;
  border-radius: 0.25rem;
  font-size: 1rem;
  font-weight: 300;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
  letter-spacing: 0.5px;
  text-decoration: none;
}

.precommand-link:hover {
  background-color: var(--color-hover);
  color: var(--color-primary);
}

.precommand-link:active {
  color: var(--color-primary);
}

.summary-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.summary-body {
 background-color: var(--tab-default);
  padding: 0 20px;
}

.summary-section {
  margin-bottom: 1rem;
  padding: 1rem;
  color: var(--color-text-primary);
  text-align: center;
}

.summary-section:last-child {
  margin-bottom: 0;
}


.grade-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.grade-name,
.title-color {
  margin: 1rem 0 0 0;
  font-size: 1.625rem;
  line-height: 1;
  font-weight: 700;
}

.grade-motorization,
.grade-battery {
  margin: 0;
  font-size: 1rem;
  line-height: 1;
  font-weight: 400;
  color: var(--color-text-primary);
}

.grade-color {
  margin: 0;
  font-size: 1rem;
  line-height: 1;
  font-weight: 400;
  color: var(--color-text-primary);
}

.grade-price {
  margin: 0;
  font-size: 14px;
  color: #007bff;
  font-weight: 500;
}

.color-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.color-label {
  margin: 0;
  font-size: 14px;
  color: #666;
  text-align: center;
}

.empty {
  margin: 0;
  font-style: italic;
  color: #999;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 768px) {
  .summary-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    padding: 0 10px;
  }

  .reset-btn {
    justify-content: center;
  }

  .summary-body {
    padding: 0 10px;
  }

  .summary-section {
    margin-bottom: 20px;
  }
}
</style>